import React, { useMemo } from "react";
import TaskDocumentationTable from "./taskDocumentationTable/taskDocumentationTable.tsx";
import { useTaskDocumentationHooks } from "./TaskDocumentation.hooks";
import { DotLegalHeader, DotLegalRadioButton, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import DotLegalFileUploadWrapper from "../../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { Box, Collapse, IconButton, RadioGroup, Typography } from "@mui/material";
import DotLegalWarningTriangle from "../../../common/components/dotLegalWarningTriangle/DotLegalWarningTriangle";
import { EntityType } from "../../../common/Common.types";
import DotLegalSection from "../../../common/components/dotLegalSection/DotLegalSection.tsx";
import CloseIcon from "@mui/icons-material/Close";
import { useDocumentPreviewManager } from "../../../common/components/documentPreview/useDocumentPreviewManager";
import TaskPolicies from "./taskPolicies/TaskPolicies.tsx";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface ITaskDocumentation {
    taskId: string;
    taskName?: string;
    updateTaskState: () => void;
    updateDocumentState: (hasDocuments: boolean) => void;
    disabled: boolean;
    allDataIsDoneLoading: boolean;
    shouldAddDocument?: boolean;
    show?: boolean;
    isDocumentationRequired?: boolean;
    onFileDialogClose?: () => void;
    onClose: () => void;
    passIsDoneLoading?: (newValue: boolean) => void;
    isPdf?: boolean;
}

function TaskDocumentation(props: ITaskDocumentation) {
    const { translateString } = useTranslation();
    const {
        documentsData,
        createDocuments,
        removeDocument,
        documentsUploading,
        uploadPercentage,
        documentationOption,
        setDocumentationOption,
        taskLink,
        setTaskLink,
        uploadLink,
        updateDocument,
        selectedDocument,
        setSelectedDocument,
        refetch,
        removePolicy,
        describeDocument,
        describingDocumentId,
        showAiDescribe,
    } = useTaskDocumentationHooks(props, (hasDocuments) => props.updateDocumentState(hasDocuments));

    const previewableDocuments = useMemo(() => {
        return (documentsData ?? []).map((doc) => ({
            documentId: doc.id,
            name: doc.name ?? doc.link ?? "",
            fileName: doc.name,
            link: doc.link,
            dataId: doc.dataId,
        }));
    }, [documentsData]);

    const { handleDocumentClick } = useDocumentPreviewManager({
        documents: previewableDocuments,
        entityType: EntityType.Task,
        entityId: props.taskId,
        entityTitle: props.taskName,
        // A task document opens as a pane on the stage rather than a fullscreen dialog, so the task it
        // belongs to stays on screen beside it.
        documentEntry: {},
        openBesideCurrentPane: true,
    });

    const documents = documentsData ? (props.isPdf ? documentsData?.filter((x) => x.isPolicy) : documentsData) : [];

    const shouldShowDocuments = props.show && props.allDataIsDoneLoading;

    const showCloseButton = documents.length === 0 && !props.isDocumentationRequired;
    return (
        <>
            {shouldShowDocuments && (
                <Box
                    sx={{
                        position: "relative",
                        "&:hover .documentation-close-button": {
                            visibility: "visible",
                        },
                    }}
                >
                    <>
                        {props.isPdf && documents.length > 0 ? (
                            <DotLegalSection>
                                <TaskDocumentationTable
                                    documents={documents}
                                    onRemoveDocument={() => {}}
                                    disabled={props.disabled}
                                    onEditDocument={() => {}}
                                    selectedDocument={undefined}
                                    setSelectedDocument={() => {}}
                                    entityType={EntityType.Task}
                                    entityId={props.taskId}
                                    onRemovePolicy={() => {}}
                                />
                            </DotLegalSection>
                        ) : (
                            <DotLegalSection>
                                <DotLegalHeader marginTop={0} marginBottom={0.5} headerStyle="small">
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {translateString("addDocumentation")}
                                        {props.isDocumentationRequired && <DotLegalWarningTriangle warningText={translateString("documentationIsRequired")} />}
                                    </Box>
                                </DotLegalHeader>

                                {showCloseButton && (
                                    <IconButton
                                        className="documentation-close-button"
                                        aria-label="close"
                                        onClick={props.onClose}
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            padding: 0.5,
                                            visibility: "hidden",
                                        }}
                                    >
                                        <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                    </IconButton>
                                )}

                                <Box>
                                    <RadioGroup
                                        row
                                        value={documentationOption}
                                        onChange={(e) => {
                                            setDocumentationOption(Number((e.target as HTMLInputElement).value));
                                            setTaskLink(undefined);
                                        }}
                                    >
                                        <DotLegalRadioButton
                                            disabled={props.disabled}
                                            value={1}
                                            labelPlacement="end"
                                            label={translateString("uploadDocument")}
                                        />
                                        <DotLegalRadioButton disabled={props.disabled} value={0} labelPlacement="end" label={translateString("insertLink")} />
                                        <DotLegalRadioButton disabled={props.disabled} value={2} labelPlacement="end" label={translateString("linkPolicy")} />
                                    </RadioGroup>
                                    <Collapse in={documentationOption === 1}>
                                        {
                                            <DotLegalFileUploadWrapper
                                                disabled={props.disabled}
                                                files={[]}
                                                onFilesChanged={async (files) => {
                                                    await createDocuments(files);
                                                    await props.updateTaskState();
                                                    await props.updateDocumentState(true);
                                                }}
                                                shouldOpenFileDialog={props.shouldAddDocument}
                                                isLoading={documentsUploading}
                                                onFileDialogCancel={props.onFileDialogClose}
                                                pasteEnabled={documentationOption === 1}
                                            />
                                        }
                                        <UploadProgressBar isUploading={documentsUploading} uploadPercentage={uploadPercentage} />
                                    </Collapse>

                                    <Collapse in={documentationOption === 0}>
                                        <Box sx={{ height: 70, display: "flex", flexDirection: "column", "& > *": { marginTop: 0 } }}>
                                            <DotLegalTextField
                                                label={translateString("insertLink")}
                                                value={taskLink ?? null}
                                                debounce={false}
                                                errorText={""}
                                                onChange={(value) => {
                                                    setTaskLink(value);
                                                }}
                                                disabled={props.disabled}
                                                noMargin
                                            ></DotLegalTextField>
                                            <Typography
                                                onClick={uploadLink}
                                                sx={(theme) => ({
                                                    alignSelf: "end",
                                                    fontSize: theme.typography.pxToRem(12),
                                                    color: theme.palette.primary.main,
                                                    "&:hover": {
                                                        cursor: "pointer",
                                                        textDecoration: "underline",
                                                    },
                                                })}
                                            >
                                                {translateString("insertLink")}
                                            </Typography>
                                        </Box>
                                    </Collapse>
                                    <Collapse in={documentationOption === 2}>
                                        <TaskPolicies
                                            onPoliciesAdded={() => {
                                                props.updateDocumentState(true);
                                                props.updateTaskState();
                                                refetch();
                                            }}
                                            taskId={props.taskId}
                                            addedPolicies={documents?.filter((x) => x.isPolicy).map((x) => x.id) ?? []}
                                        />
                                    </Collapse>
                                </Box>

                                {documents.length > 0 && (
                                    <TaskDocumentationTable
                                        documents={documents}
                                        onRemoveDocument={async (documentId) => {
                                            await removeDocument(documentId);
                                        }}
                                        disabled={props.disabled}
                                        onEditDocument={async (document) => {
                                            await updateDocument(document);
                                        }}
                                        selectedDocument={selectedDocument}
                                        setSelectedDocument={setSelectedDocument}
                                        entityType={EntityType.Task}
                                        entityId={props.taskId}
                                        onDocumentClick={(doc) =>
                                            handleDocumentClick({
                                                documentId: doc.id,
                                                name: doc.name ?? doc.link ?? "",
                                                fileName: doc.name,
                                                link: doc.link,
                                                dataId: doc.dataId,
                                            })
                                        }
                                        onRemovePolicy={(id: string) => removePolicy(id)}
                                        onDescribeDocument={(documentId) => describeDocument(documentId)}
                                        describingDocumentId={describingDocumentId}
                                        showAiDescribe={showAiDescribe}
                                    />
                                )}
                            </DotLegalSection>
                        )}
                    </>
                </Box>
            )}
        </>
    );
}

export default TaskDocumentation;
